import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Loader2, TrendingUp, Target, Building2, ShieldCheck, AlertTriangle, Briefcase, ChevronRight, Activity } from 'lucide-react';
import { cn } from '../lib/utils';

export default function UnifiedInsightsDashboard({ onNavigate }) {
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('30d');

    const loadInsights = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/api/sales-reporting/unified-insights?period=${period}`);
            setInsights(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error?.message || 'Failed to load unified insights.');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        loadInsights();
    }, [loadInsights]);

    const renderTrend = (trendData) => {
        if (!trendData || trendData.percentageChange === null) return <span className="text-muted-foreground text-xs mt-1 block font-medium">Static / No baseline</span>;

        let color = 'text-muted-foreground';
        let arrow = '';
        if (trendData.trendDirection === 'UP') {
            color = 'text-emerald-500';
            arrow = '↑';
        } else if (trendData.trendDirection === 'DOWN') {
            color = 'text-rose-500';
            arrow = '↓';
        }

        return (
            <span className={cn("text-xs mt-1 font-medium flex items-center gap-1", color)}>
                {arrow} {Math.abs(trendData.percentageChange)}% vs prior
            </span>
        );
    };

    const renderInvertedTrend = (trendData) => {
        if (!trendData || trendData.percentageChange === null) return <span className="text-muted-foreground text-xs mt-1 block font-medium">Static / No baseline</span>;

        let color = 'text-muted-foreground';
        let arrow = '';
        if (trendData.trendDirection === 'UP') {
            color = 'text-rose-500'; // UP is bad for risk/escalations
            arrow = '↑';
        } else if (trendData.trendDirection === 'DOWN') {
            color = 'text-emerald-500'; // DOWN is good for risk/escalations
            arrow = '↓';
        }

        return (
            <span className={cn("text-xs mt-1 font-medium flex items-center gap-1", color)}>
                {arrow} {Math.abs(trendData.percentageChange)}% vs prior
            </span>
        );
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);

    return (
        <div className="space-y-6 max-w-7xl mx-auto text-foreground">
            {/* Header & Controls */}
            <div className="flex justify-between items-center bg-card p-4 rounded-xl shadow-sm border border-border">
                <div>
                    <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary" /> Executive Insights
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">Unified velocity across Sales, Success, and Commercial posture.</p>
                </div>
                <select
                    value={period}
                    onChange={e => setPeriod(e.target.value)}
                    className="bg-background border-border rounded-lg text-sm font-medium shadow-sm focus:border-primary focus:ring-primary cursor-pointer text-foreground transition-colors"
                >
                    <option value="7d">Trailing 7 Days</option>
                    <option value="30d">Trailing 30 Days</option>
                    <option value="90d">Trailing 90 Days</option>
                </select>
            </div>

            {loading ? (
                <div className="h-64 flex items-center justify-center bg-card rounded-xl border border-border">
                    <div className="flex flex-col items-center">
                        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                        <p className="text-sm text-muted-foreground font-medium animate-pulse">Aggregating cross-functional reports...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="p-8 text-destructive bg-destructive/10 rounded-xl border border-destructive/20 shadow-sm max-w-2xl">{error}</div>
            ) : insights ? (
                <div className="space-y-8">
                    {/* 1. SALES VELOCITY BLOCK */}
                    <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-muted/30 border-b border-border px-6 py-4 flex items-center justify-between">
                            <h3 className="text-base font-bold flex items-center gap-2">
                                <Target className="w-5 h-5 text-primary" /> Sales & Revenue Trajectory
                            </h3>
                            <button onClick={() => onNavigate && onNavigate('revenue')} className="text-xs font-bold text-primary flex items-center hover:opacity-80 transition-opacity">
                                View Full Dashboard <ChevronRight className="w-3 h-3 ml-0.5" />
                            </button>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-background rounded-xl border border-border p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Open Pipeline Growth</div>
                                <div className="text-3xl font-black text-foreground">{formatCurrency(insights.sales?.openPipelineValue?.current)}</div>
                                <div className="mt-2 pt-2 border-t border-border">
                                    {renderTrend(insights.sales?.openPipelineValue)}
                                </div>
                            </div>

                            <div className="bg-background rounded-xl border border-emerald-500/20 p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                                <div className="absolute opacity-5 -right-2 -top-2"><TrendingUp className="w-24 h-24 text-emerald-500" /></div>
                                <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider mb-2 relative z-10">Won Revenue</div>
                                <div className="text-3xl font-black text-foreground relative z-10">{formatCurrency(insights.sales?.wonValue?.current)}</div>
                                <div className="mt-2 pt-2 border-t border-emerald-500/10 relative z-10">
                                    {renderTrend(insights.sales?.wonValue)}
                                </div>
                            </div>

                            <div className="bg-background rounded-xl border border-rose-500/20 p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mb-2">Lost Value</div>
                                <div className="text-3xl font-black text-foreground">{formatCurrency(insights.sales?.lostValue?.current)}</div>
                                <div className="mt-2 pt-2 border-t border-rose-500/10">
                                    {renderInvertedTrend(insights.sales?.lostValue)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. SUCCESS POSTURE BLOCK */}
                    <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-primary/5 border-b border-border px-6 py-4 flex items-center justify-between">
                            <h3 className="text-base font-bold flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-primary" /> Customer Success Posture
                            </h3>
                            <button onClick={() => onNavigate && onNavigate('success')} className="text-xs font-bold text-primary flex items-center hover:opacity-80 transition-opacity">
                                View CS Operations <ChevronRight className="w-3 h-3 ml-0.5" />
                            </button>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-background rounded-xl border border-orange-500/20 p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                                <div className="absolute opacity-5 -right-2 -top-2"><AlertTriangle className="w-24 h-24 text-orange-500" /></div>
                                <div className="text-[10px] font-bold text-orange-500 uppercase tracking-wider mb-2 relative z-10">Risk Escalations</div>
                                <div className="text-3xl font-black text-foreground relative z-10">{insights.success?.highRiskEscalations?.current || 0} <span className="text-base font-medium text-muted-foreground -ml-1">accounts</span></div>
                                <div className="mt-2 pt-2 border-t border-orange-500/10 relative z-10">
                                    {renderInvertedTrend(insights.success?.highRiskEscalations)}
                                </div>
                            </div>

                            <div className="bg-background rounded-xl border border-border p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Onboarding Conversions</div>
                                <div className="text-3xl font-black text-foreground">{insights.success?.onboardedAccounts?.current || 0} <span className="text-base font-medium text-muted-foreground -ml-1">graduated</span></div>
                                <div className="mt-2 pt-2 border-t border-border">
                                    {renderTrend(insights.success?.onboardedAccounts)}
                                </div>
                            </div>

                            <div className="bg-background rounded-xl border border-border p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Tasks Completed</div>
                                <div className="text-3xl font-black text-foreground">{insights.success?.tasksClosed?.current || 0} <span className="text-base font-medium text-muted-foreground -ml-1">closed</span></div>
                                <div className="mt-2 pt-2 border-t border-border">
                                    {renderTrend(insights.success?.tasksClosed)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. COMMERCIAL / MEMBERSHIP BLOCK */}
                    <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-muted/20 border-b border-border px-6 py-4 flex items-center justify-between">
                            <h3 className="text-base font-bold flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-primary" /> Workspace Commercial State
                            </h3>
                            <button onClick={() => onNavigate && onNavigate('settings')} className="text-xs font-bold text-primary flex items-center hover:opacity-80 transition-opacity">
                                Manage Subscription <ChevronRight className="w-3 h-3 ml-0.5" />
                            </button>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-muted/5">
                            <div className="bg-background rounded-xl p-6 border border-border shadow-sm flex items-start gap-4">
                                <div className="p-3 bg-primary/10 text-primary rounded-xl">
                                    <Briefcase className="w-6 h-6" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Active Plan Tier</div>
                                    <div className="text-2xl font-black text-foreground uppercase tracking-tight">{insights.commercial?.plan || 'Free'}</div>
                                    <div className="text-sm font-medium text-emerald-500 mt-1 flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div> {insights.commercial?.status || 'ACTIVE'}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-background rounded-xl p-6 border border-border shadow-sm flex items-start flex-col justify-center">
                                <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Commercial Segment</div>
                                <div className="text-xl font-bold text-foreground">{insights.commercial?.tier || 'Standard'} License</div>
                                {insights.commercial?.renewalTerm && (
                                    <div className="text-xs text-muted-foreground mt-2 bg-muted/40 border border-border rounded-lg px-3 py-2 font-medium">
                                        Renews: {new Date(insights.commercial.renewalTerm).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
